import { useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import { useDeleteAction, useResetAction } from '@/models/article';
import { ArticleActionWithRelations, ArticleWithRelations } from '@/models/request';
import useViewPort from '@/utils/useViewport';

type AcceptOrRefuse = 'accept' | 'refuse';

export const AcceptRefuseServiceRadioButtons = ({
  action,
  isPreviousAction,
  article,
}: {
  action: ArticleActionWithRelations;
  isPreviousAction: boolean;
  article: ArticleWithRelations;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const { mutate: deleteAction } = useDeleteAction({
    articleId: article.id,
    requestId: article.requestId,
  });

  const { mutate: resetAction } = useResetAction({
    articleId: article.id,
    requestId: article.requestId,
  });

  const [value, setValue] = useState<AcceptOrRefuse>(isPreviousAction ? 'refuse' : 'accept');

  const acceptMessage = t({ id: 'client.article.services.accept', message: 'Accept' });
  const refuseMessage = t({ id: 'client.article.services.refuse', message: 'Refuse' });

  const _hasNoOtherCurrentActionsAccepted = hasNoOtherCurrentActionsAccepted(
    action.id,
    article.snapshot.articleActions
  );

  const handleChange = (newValue: AcceptOrRefuse) => {
    if (newValue === value) {
      return;
    }

    setValue(newValue);

    if (newValue === 'refuse') {
      deleteAction(action.id);
    }

    if (newValue === 'accept') {
      resetAction(action.stableId);
    }
  };

  const radioItemGroup = isMobile ? (
    <MobileRadioItemsButtonGroup
      value={value}
      acceptMessage={acceptMessage}
      refuseMessage={refuseMessage}
      hasNoOtherCurrentActionsAccepted={_hasNoOtherCurrentActionsAccepted}
      onChange={handleChange}
    />
  ) : (
    <RadioItemsButtonGroup
      value={value}
      acceptMessage={acceptMessage}
      refuseMessage={refuseMessage}
      hasNoOtherCurrentActionsAccepted={_hasNoOtherCurrentActionsAccepted}
      onChange={handleChange}
    />
  );

  if (_hasNoOtherCurrentActionsAccepted) {
    return (
      <Tooltip
        content={t({
          id: 'client.article.services.at-least-one-service-tooltip',
          message:
            'At least one service must remain in the proposal. To remove all, click the "Refuse services" button.',
        })}
      >
        <Button variant="style-less">{radioItemGroup}</Button>
      </Tooltip>
    );
  }

  return radioItemGroup;
};

/**
 * If there is no other action that has been accepted, then return false.
 * It will allow us to prevent the refusal of the action in question.
 */
const hasNoOtherCurrentActionsAccepted = (
  actionId: string,
  acceptedActions: ArticleActionWithRelations[]
): boolean => {
  const allOtherNonChildCurrentActions = acceptedActions.filter(
    (currentAction) => currentAction.id !== actionId && !currentAction.parents.length
  );

  return !allOtherNonChildCurrentActions.length;
};

const RadioItemsButtonGroup = ({
  value,
  onChange,
  hasNoOtherCurrentActionsAccepted,
  acceptMessage,
  refuseMessage,
}: {
  value: AcceptOrRefuse;
  onChange: (value: AcceptOrRefuse) => void;
  hasNoOtherCurrentActionsAccepted: boolean;
  acceptMessage: string;
  refuseMessage: string;
}) => {
  return (
    <Stack gap="0.25rem">
      <RadioItemGroup
        name="acceptRefuseService"
        direction="column"
        alignment="end"
        theme="brand"
        value={value}
        onChange={onChange}
        disabled={hasNoOtherCurrentActionsAccepted}
      >
        <RadioItem value="accept" ariaLabel={acceptMessage}>
          {acceptMessage}
        </RadioItem>
        <RadioItem value="refuse" ariaLabel={refuseMessage}>
          {refuseMessage}
        </RadioItem>
      </RadioItemGroup>
    </Stack>
  );
};

const MobileRadioItemsButtonGroup = ({
  value,
  onChange,
  hasNoOtherCurrentActionsAccepted,
  acceptMessage,
  refuseMessage,
}: {
  value: AcceptOrRefuse;
  onChange: (value: AcceptOrRefuse) => void;
  hasNoOtherCurrentActionsAccepted: boolean;
  acceptMessage: string;
  refuseMessage: string;
}) => {
  return (
    <RadioBoxGroup
      name="acceptRefuseService"
      direction="row"
      theme="brand"
      value={value}
      onChange={onChange}
      disabled={hasNoOtherCurrentActionsAccepted}
      style={{
        display: 'flex',
      }}
    >
      <RadioBox value="refuse" ariaLabel={refuseMessage} title={refuseMessage}></RadioBox>
      <RadioBox value="accept" ariaLabel={acceptMessage} title={acceptMessage}></RadioBox>
    </RadioBoxGroup>
  );
};
